/* ============================================================
   Catharsis — global behaviours (vanilla, no dependencies)
   Calm motion, accessible, performance-first.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Reveal on scroll (fade + 8px up) ---------- */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function nodesWithin(root, selector) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = [];
    if (scope.nodeType === 1 && scope.matches(selector)) nodes.push(scope);
    scope.querySelectorAll(selector).forEach(function (node) { nodes.push(node); });
    return nodes;
  }

  var revealObserver = null;
  function initReveal(root, revealImmediately) {
    var els = nodesWithin(root, '.reveal');
    if (reduceMotion || revealImmediately || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      // threshold 0, jamais une fraction : un seuil de 10 % ne se franchit pas quand l'élément
      // est plus de dix fois plus haut que l'écran. Mesuré le 13 septembre sur Android : le
      // contrat de garantie commerciale fait 8 144 px, l'écran en montre 9,3 % au mieux — le texte
      // restait à opacité 0 sur toute la page, et sur le thème publié aussi.
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    }
    els.forEach(function (el) {
      if (!el.classList.contains('is-visible')) revealObserver.observe(el);
    });
  }

  function destroyReveal(root) {
    if (!revealObserver) return;
    nodesWithin(root, '.reveal').forEach(function (el) { revealObserver.unobserve(el); });
  }

  /* ---------- Hero background video (LCP-safe, decorative) ----------
     Lazily activates the optional muted/looping background video layered over the hero's
     product image WITHOUT ever competing with LCP. The <img> stays the LCP element and the
     universal fallback; the <video> ships preload="none" with its <source> URLs held in
     data-src (never auto-loaded). Activation is triple-gated:
       1. prefers-reduced-motion must NOT be set (we bail entirely otherwise — poster stays),
       2. the hero tile must enter the viewport (IntersectionObserver), AND
       3. the main thread must be idle (requestIdleCallback, setTimeout fallback)
     before we copy data-src -> src, call .load() then .play(). Only on the `playing` event do
     we fade the video in over the image. Any error / stall / blocked autoplay leaves the image
     visible (the video stays opacity 0). No audio; decorative (aria-hidden, tabindex=-1);
     no-JS -> image only. */
  var heroVideoObserver = null;
  function initHeroVideo(root) {
    if (reduceMotion) return;
    var videos = nodesWithin(root, '[data-hero-video]');
    if (!videos.length || !('IntersectionObserver' in window)) return;

    function activate(video) {
      if (!video.isConnected || video.dataset.heroVideoReady === 'true') return;
      var sources = video.querySelectorAll('source[data-src]');
      if (!sources.length) return;
      var hasSrc = false;
      sources.forEach(function (s) {
        var url = s.getAttribute('data-src');
        if (url && !s.getAttribute('src')) { s.setAttribute('src', url); hasSrc = true; }
      });
      if (!hasSrc) return;
      video.dataset.heroVideoReady = 'true';
      video.muted = true; // satisfy autoplay policy even if the attribute is dropped
      video.addEventListener('playing', function () { video.classList.add('is-playing'); }, { once: true });
      video.load();
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () { /* blocked / error -> poster image stays visible */ });
      }
    }

    if (!heroVideoObserver) {
      heroVideoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var video = entry.target;
          heroVideoObserver.unobserve(video);
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(function () { activate(video); }, { timeout: 2000 });
          } else {
            setTimeout(function () { activate(video); }, 200);
          }
        });
      }, { rootMargin: '200px 0px' });
    }

    videos.forEach(function (v) {
      if (v.dataset.heroVideoReady !== 'true') heroVideoObserver.observe(v);
    });
  }

  function destroyHeroVideo(root) {
    if (!heroVideoObserver) return;
    nodesWithin(root, '[data-hero-video]').forEach(function (video) { heroVideoObserver.unobserve(video); });
  }

  /* ---------- Sticky header shadow on scroll ---------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var onScroll = function () {
      if (window.scrollY > 8) header.setAttribute('data-scrolled', '');
      else header.removeAttribute('data-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Quantity stepper ---------- */
  function initQtySteppers() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-step]');
      if (!btn || btn.disabled) return;
      var wrap = btn.closest('[data-qty]');
      if (!wrap) return;
      var input = wrap.querySelector('input[type="number"]');
      if (!input || input.disabled) return;
      var step = parseInt(btn.getAttribute('data-qty-step'), 10) || 0;
      var min = parseInt(input.min, 10) || 1;
      var next = (parseInt(input.value, 10) || min) + step;
      if (next < min) next = min;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Accordion (FAQ / spec groups) ---------- */
  function initAccordions() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;
      var item = trigger.closest('[data-accordion-item]');
      if (!item) return;
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      var panel = item.querySelector('[data-accordion-panel]');
      if (panel) panel.hidden = expanded;
    });
  }

  /* ---------- Content tabs (ARIA tablist) ----------
     Progressive enhancement for blocks/product-content-tabs.liquid. No-JS = every panel visible
     under its own heading (crawler-safe, nothing gated). This island upgrades that into an ARIA
     tablist: roving tabindex, ArrowLeft/Right/Up/Down + Home/End, click. Inactive panels keep their
     content in the DOM (just `hidden`), so crawlers/LLMs still read them. With fewer than two tabs we
     leave the content stacked (collapsing a single panel adds nothing). */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tabs-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-tabs-panel]'));
      if (tabs.length < 2 || tabs.length !== panels.length) return;
      root.classList.add('is-enhanced');

      function activate(index, focusTab) {
        tabs.forEach(function (tab, i) {
          var selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
          panels[i].hidden = !selected;
        });
        if (focusTab && tabs[index]) tabs[index].focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(i, false); });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = tabs.length - 1;
          if (next !== null) { e.preventDefault(); activate(next, true); }
        });
      });

      activate(0, false);
    });
  }

  /* ---------- Focus management (trap + restore) ---------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function focusableWithin(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }
  // Keeps Tab/Shift+Tab inside `panel`. Returns a handler to remove later.
  function trapFocus(panel) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = focusableWithin(panel);
      if (!items.length) { e.preventDefault(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    panel.addEventListener('keydown', onKeydown);
    return function () { panel.removeEventListener('keydown', onKeydown); };
  }

  /* ---------- Cart drawer (custom element) ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.panel = this.querySelector('[data-drawer-panel]');
      this.closeBtns = this.querySelectorAll('[data-drawer-close]');
      this._lastFocus = null;
      this._releaseTrap = null;
      this.closeBtns.forEach((b) => b.addEventListener('click', () => this.close()));
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.hasAttribute('open')) this.close(); });
      document.addEventListener('click', (e) => {
        var opener = e.target.closest('[data-cart-open]');
        if (opener) { e.preventDefault(); this.open(); }
      });
      document.addEventListener('cart:updated', (e) => this.render(e.detail));
    }
    open() {
      this._lastFocus = document.activeElement;
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      var f = this.querySelector('[data-drawer-close]');
      if (f) f.focus();
      if (this.panel) this._releaseTrap = trapFocus(this.panel);
    }
    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.style.overflow = '';
      if (this._releaseTrap) { this._releaseTrap(); this._releaseTrap = null; }
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') this._lastFocus.focus();
      this._lastFocus = null;
    }
    render(state) {
      if (state && typeof state.item_count === 'number') {
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = state.item_count;
          el.hidden = state.item_count === 0;
        });
        // The badge itself is aria-hidden — a lone "0" is a label/name mismatch — so the cart
        // link's accessible name is the only place assistive tech reads the count. Keep it in
        // sync here rather than re-fetching a translation.
        document.querySelectorAll('[data-cart-open][data-cart-label-template]').forEach((link) => {
          const tpl = link.getAttribute('data-cart-label-template');
          if (tpl) link.setAttribute('aria-label', tpl.replace('__n__', String(state.item_count)));
        });
      }
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ---------- Predictive search (overlay + live results island) ----------
     Custom element mirroring the cart-drawer idiom. Opens an accessible overlay
     (role="dialog", focus-trapped), then fetches the `predictive-search` section
     fragment as the user types and injects it as an ARIA listbox.

     Correctness guarantees:
       - Debounced (250ms); only fetches when the trimmed term length >= 2. Below 2 → clears.
       - Race-safe: an AbortController aborts the previous in-flight request before each new
         fetch, so a slow OLDER response can never overwrite a NEWER query's results.
       - The fetched options are taken out of the Tab order (tabindex=-1); navigation is the
         combobox virtual-focus pattern (ArrowUp/Down move aria-activedescendant; clicks still
         work). Enter on a highlighted option navigates; with none highlighted the form submits
         natively to routes.search_url (no-JS-equivalent full search).
       - The aria-live status region is persistent in the header and updated once per settled
         fetch (count or "no results"), never per keystroke. */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('form');
      this.resultsEl = this.querySelector('[data-search-results]');
      this.statusEl = this.querySelector('[data-search-status]');
      this.backdrop = this.querySelector('[data-search-backdrop]');
      this.panel = this.querySelector('[data-search-panel]');
      this.predictiveUrl = this.getAttribute('data-predictive-url') || '/search/suggest';
      this.sectionId = this.getAttribute('data-section-id') || 'predictive-search';
      this.limit = this.getAttribute('data-results-limit') || '6';
      this._controller = null;
      this._debounce = null;
      this._lastFocus = null;
      this._releaseTrap = null;
      this._options = [];
      this._activeIndex = -1;
      if (!this.input || !this.resultsEl) return;

      document.addEventListener('click', (e) => {
        var opener = e.target.closest('[data-search-open]');
        if (opener) { e.preventDefault(); this.open(); }
      });
      this.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.input.addEventListener('input', () => this.onInput());
      this.addEventListener('keydown', (e) => this.onKeydown(e));
      this.resultsEl.addEventListener('mousemove', (e) => {
        var opt = e.target.closest('[role="option"]');
        if (opt) { var i = this._options.indexOf(opt); if (i > -1) this.setActive(i); }
      });
      // Options carry their destination on data-predictive-url and (per ARIA) hold NO interactive
      // descendants, so the island owns click navigation just like it owns Enter. Works for product
      // cards (now linkless) and the link rows alike.
      this.resultsEl.addEventListener('click', (e) => {
        var opt = e.target.closest('[role="option"]');
        if (!opt) return;
        var url = opt.getAttribute('data-predictive-url');
        if (url) { e.preventDefault(); window.location.href = url; }
      });
    }

    open() {
      if (this.hasAttribute('open')) return;
      this._lastFocus = document.activeElement;
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      document.querySelectorAll('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      if (this.panel) this._releaseTrap = trapFocus(this.panel);
      requestAnimationFrame(() => this.input.focus());
    }

    close() {
      if (!this.hasAttribute('open')) return;
      clearTimeout(this._debounce);
      this._abort();
      this.removeAttribute('open');
      document.body.style.overflow = '';
      document.querySelectorAll('[data-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (this._releaseTrap) { this._releaseTrap(); this._releaseTrap = null; }
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') this._lastFocus.focus();
      this._lastFocus = null;
    }

    _abort() { if (this._controller) { this._controller.abort(); this._controller = null; } }

    onInput() {
      var term = this.input.value.trim();
      clearTimeout(this._debounce);
      if (term.length < 2) { this.clearResults(); return; }
      this._debounce = setTimeout(() => this.fetchResults(term), 250);
    }

    clearResults() {
      this._abort();
      this.resultsEl.innerHTML = '';
      this._options = [];
      this._activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.statusEl) this.statusEl.textContent = '';
    }

    fetchResults(term) {
      this._abort();
      this._controller = new AbortController();
      var url = this.predictiveUrl
        + '?q=' + encodeURIComponent(term)
        + '&section_id=' + encodeURIComponent(this.sectionId)
        + '&resources[type]=product,collection,page,query'
        + '&resources[limit]=' + encodeURIComponent(this.limit)
        + '&resources[options][unavailable_products]=last';
      fetch(url, { signal: this._controller.signal, headers: { 'Accept': 'text/html' } })
        .then((r) => r.text())
        .then((html) => this.render(html))
        .catch((err) => { if (err.name !== 'AbortError') this.clearResults(); });
    }

    render(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-predictive-content]');
      this.resultsEl.innerHTML = fresh ? fresh.outerHTML : '';
      // Take result links/buttons out of Tab order — virtual focus via aria-activedescendant.
      this.resultsEl.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      this._options = Array.prototype.slice.call(this.resultsEl.querySelectorAll('[role="option"]'));
      this._activeIndex = -1;
      this.input.setAttribute('aria-expanded', this._options.length ? 'true' : 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (this.statusEl) {
        var carrier = this.resultsEl.querySelector('[data-predictive-status]');
        this.statusEl.textContent = carrier ? carrier.textContent.trim() : '';
      }
    }

    setActive(index) {
      if (!this._options.length) return;
      if (this._activeIndex > -1 && this._options[this._activeIndex]) {
        this._options[this._activeIndex].setAttribute('aria-selected', 'false');
      }
      if (index < 0) index = this._options.length - 1;
      else if (index >= this._options.length) index = 0;
      this._activeIndex = index;
      var opt = this._options[index];
      opt.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', opt.id);
      if (typeof opt.scrollIntoView === 'function') opt.scrollIntoView({ block: 'nearest' });
    }

    clearActive() {
      if (this._activeIndex > -1 && this._options[this._activeIndex]) {
        this._options[this._activeIndex].setAttribute('aria-selected', 'false');
      }
      this._activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (!this._options.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); this.setActive(this._activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActive(this._activeIndex - 1); }
      else if (e.key === 'Enter' && this._activeIndex > -1) {
        var url = this._options[this._activeIndex].getAttribute('data-predictive-url');
        if (url) { e.preventDefault(); window.location.href = url; }
        // No highlight → default form submit (GET routes.search_url) performs a full search.
      }
    }
  }

  function initPredictiveSearch() {
    if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  }

  /* ---------- Cart helpers (AJAX + Section Rendering API) ---------- */
  function cartHasFranceOffer(cart) {
    return (cart.items || []).some(function (item) {
      return (item.properties && item.properties._catharsis_offer === 'france') ||
        item.handle === 'pack-francais-catlang-cat-mat' ||
        Number(item.product_id) === 11185440358737;
    });
  }

  function assertFrenchOffersReady(cart, transactions) {
    if (!transactions || typeof transactions.invalidFrenchPackAttempts !== 'function') {
      throw new Error('La préparation du panier n’est pas terminée. Réessayez dans un instant.');
    }
    if (!transactions.invalidFrenchPackAttempts(cart).length) return;
    if ((cart.items || []).some(function (item) {
      return Number(item.product_id) === 11185440358737 ||
        (item.properties && item.properties._catharsis_offer === 'france' &&
          item.properties._catharsis_bundle_native !== 'true');
    })) {
      throw new Error('Retirez votre ancien Pack Français du panier, puis ajoutez la formule actuelle pour bénéficier des 56 € d’économies.');
    }
    throw new Error('Le prix, la remise ou les articles inclus du Pack Français ne correspondent pas à l’offre. Vérifiez votre panier avant le paiement.');
  }

  function refreshCartDrawer(errorMessage) {
    var drawer = document.querySelector('cart-drawer');
    var sectionId = drawer ? drawer.getAttribute('data-section-id') : null;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        // A stale drawer can still contain express buttons while Section Rendering is in
        // flight or unavailable. Remove that bypass as soon as the server cart has a Pack.
        if (drawer && cartHasFranceOffer(cart)) {
          drawer.querySelectorAll('.cart-drawer__express, .cart-drawer__or').forEach(function (node) { node.remove(); });
        }
        // The cart page is server-rendered.  Replacing only the drawer would leave its line
        // keys, quantities and checkout total stale after a paired mutation, so reload its
        // source DOM rather than presenting a cart different from Shopify's cart.
        if (document.querySelector('[data-cart-page-form]')) {
          if (errorMessage) {
            try { sessionStorage.setItem('catharsis:cart-error', errorMessage); } catch (_) { showCartMutationError(errorMessage); }
          }
          cartPageReloadPending = true;
          window.location.reload();
          return cart;
        }
        if (!sectionId) return cart;
        // Le tiroir se redemande a la RACINE, sans en-tete Accept. Mesure du 13 septembre 2026 :
        // l'ancienne requete visait la page en cours avec `Accept: application/json`, et sur une
        // PAGE PRODUIT Shopify repond a cet en-tete par le produit en JSON ({"product": …}) en
        // ignorant `?sections=`. Le panier contenait l'article, et le tiroir qui s'ouvrait disait
        // « Votre panier est vide » — sur la seule page ou l'on ajoute au panier. Le rendu du
        // tiroir ne depend pas de la page : la racine le rend a l'identique, et l'API de rendu des
        // sections repond en JSON sans qu'on le lui demande.
        var racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return fetch(racine + '?sections=' + encodeURIComponent(sectionId))
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var html = data && data[sectionId];
            if (html && drawer) {
              var tmp = document.createElement('div');
              tmp.innerHTML = html;
              var fresh = tmp.querySelector('.cart-drawer__panel');
              var current = drawer.querySelector('.cart-drawer__panel');
              if (fresh && current) current.innerHTML = fresh.innerHTML;
            }
            return cart;
          });
      });
  }

  var localCartQueue = Promise.resolve();
  function enqueueCart(work) {
    var transactions = window.CatharsisCartTransactions;
    if (transactions) return transactions.enqueueCartMutation(work);
    var current = localCartQueue.then(work, work);
    localCartQueue = current.catch(function () { return undefined; });
    return current;
  }

  var cartMutationBusy = false;
  var cartPageReloadPending = false;
  var cartLockedControls = [];
  function setCartMutationBusy(busy) {
    cartMutationBusy = busy;
    var roots = document.querySelectorAll('cart-drawer, .cart-page');
    roots.forEach(function (root) {
      if (busy) root.setAttribute('aria-busy', 'true');
      else root.removeAttribute('aria-busy');
    });
    if (busy) {
      roots.forEach(function (root) {
        root.querySelectorAll('[data-cart-qty], [data-qty-step], [data-cart-remove], [data-xsell-add], button[name="checkout"], input[name="checkout"]').forEach(function (control) {
          cartLockedControls.push({ node: control, disabled: control.disabled, aria: control.getAttribute('aria-disabled'), tab: control.getAttribute('tabindex') });
          if ('disabled' in control) control.disabled = true;
          else control.setAttribute('tabindex', '-1');
          control.setAttribute('aria-disabled', 'true');
        });
      });
    } else {
      cartLockedControls.forEach(function (saved) {
        if ('disabled' in saved.node) saved.node.disabled = saved.disabled;
        if (saved.aria === null) saved.node.removeAttribute('aria-disabled');
        else saved.node.setAttribute('aria-disabled', saved.aria);
        if (saved.tab === null) saved.node.removeAttribute('tabindex');
        else saved.node.setAttribute('tabindex', saved.tab);
      });
      cartLockedControls = [];
    }
  }

  function showCartMutationError(message) {
    document.querySelectorAll('.cart-page, cart-drawer [data-drawer-content]').forEach(function (root) {
      var error = root.querySelector('[data-cart-mutation-error]');
      if (!error) {
        error = document.createElement('p');
        error.className = 'product-buy__error t-caption';
        error.setAttribute('data-cart-mutation-error', '');
        error.setAttribute('role', 'alert');
        root.prepend(error);
      }
      error.textContent = message;
      error.hidden = !message;
    });
  }

  function cartMutationMessage(error) {
    if ((document.documentElement.lang || 'fr').toLowerCase().indexOf('fr') === 0) {
      var detail = error && error.message;
      return detail && /[àâçéèêëîïôùûüœ]|\b(?:panier|article|offre)\b/i.test(detail) ? detail : 'Le panier n’a pas pu être mis à jour. Vérifiez les quantités puis réessayez.';
    }
    return 'The cart could not be updated. Check the refreshed quantities and try again.';
  }

  function changeLine(key, qty, attemptId, offerId) {
    // Lock synchronously, before queueing: a second tap must not capture a soon-obsolete key.
    if (cartMutationBusy) return Promise.resolve();
    setCartMutationBusy(true);
    showCartMutationError('');
    return enqueueCart(function () {
      var transactions = window.CatharsisCartTransactions;
      var mutation;
      if (transactions) {
        // An earlier queued add can change discount keys before this work starts. Resolve
        // only the explicitly marked France attempt; never fall back to a shared variant.
        mutation = attemptId && offerId === 'france' ? transactions.cartApi().read().then(function (cart) {
          var parent = cart.items.find(function (item) {
            return item.properties && item.properties._catharsis_attempt === attemptId &&
              item.properties._catharsis_offer === 'france' && item.properties._catharsis_component === 'catlang' && !item.parent_relationship;
          });
          if (!parent) throw new Error('Ce Pack Français n’est plus dans le panier.');
          return transactions.reconcileNestedQuantity({ key: parent.key, quantity: qty });
        }) : transactions.reconcileNestedQuantity({ key: key, quantity: qty });
      } else if (offerId === 'france') {
        // Never degrade a paired Pack into a native one-line mutation while its module is
        // missing or still loading: Shopify would leave the child quantity unsynchronised.
        mutation = Promise.reject(new Error('La préparation du panier n’est pas terminée. Réessayez dans un instant.'));
      } else {
        mutation = fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty })
        }).then(function (response) {
          if (!response.ok) throw new Error('Le panier n’a pas pu être mis à jour. Vérifiez les quantités puis réessayez.');
        });
      }
      return mutation.then(function () { return refreshCartDrawer(); }).catch(function (error) {
        var message = cartMutationMessage(error);
        // A rejected transaction may already have changed or removed lines. Always re-read
        // and render Shopify's current cart before explaining the failure to the customer.
        return refreshCartDrawer(message).catch(function () { /* keep a visible error even if offline */ })
          .then(function () { showCartMutationError(message); });
      });
    }).finally(function () { if (!cartPageReloadPending) setCartMutationBusy(false); });
  }

  function setPurchaseBusy(form, busy) {
    form.catharsisBusy = busy;
    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(function (control) {
      if (busy) {
        control.dataset.catharsisWasDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('aria-busy', 'true');
      } else {
        var wasDisabled = control.dataset.catharsisWasDisabled === 'true';
        delete control.dataset.catharsisWasDisabled;
        control.disabled = wasDisabled;
        control.setAttribute('aria-disabled', String(wasDisabled));
        control.removeAttribute('aria-busy');
      }
    });
  }

  function reportCartError(root, message) {
    var error = root.querySelector('[data-product-form-error], [data-cart-form-error]');
    if (!error) {
      error = document.createElement('p');
      error.className = 'product-buy__error t-caption';
      error.setAttribute('role', 'alert');
      error.setAttribute('data-product-form-error', '');
      if (root.matches('.cart-drawer__checkout-form, .cart-page__form')) root.prepend(error);
      else root.appendChild(error);
    }
    error.textContent = message;
    error.hidden = !message;
    if (message && root.matches('.cart-drawer__checkout-form, .cart-page__form')) {
      error.scrollIntoView({ block: 'nearest' });
    }
  }

  function addGenericItem(form) {
    var fd = new FormData(form);
    var addons = form.querySelectorAll('[data-addon-variant]:checked');
    var body = fd;
    var headers = { Accept: 'application/json' };
    if (addons.length) {
      var main = { id: fd.get('id'), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
      var plan = fd.get('selling_plan');
      if (plan) main.selling_plan = plan;
      var items = [main];
      addons.forEach(function (cb) { items.push({ id: cb.value, quantity: 1 }); });
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({ items: items });
    }
    return fetch('/cart/add.js', { method: 'POST', headers: headers, body: body })
      .then(function (r) { if (!r.ok) throw new Error('L’article n’a pas pu être ajouté.'); return r.json(); })
      .then(function () { return refreshCartDrawer(); });
  }

  function initAjaxCart() {
    // Quel bouton a soumis le formulaire ? La question n'est pas cosmetique : « Ajouter au
    // panier » et « Acheter » vivent dans le MEME formulaire (un seul id de variante, une seule
    // quantite, un seul abonnement — voir blocks/product-sticky-atc.liquid), et ils ne finissent
    // pas au meme endroit. `SubmitEvent.submitter` donne la reponse partout depuis 2022 ; le
    // repere pose au clic est la ceinture de securite, et il est pose en phase de CAPTURE pour
    // que rien ne puisse l'empecher. L'ordre clic -> submit est celui de la specification HTML,
    // y compris pour la soumission implicite au clavier, qui clique le bouton par defaut.
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('button[type="submit"], input[type="submit"]');
      if (b && b.form) b.form.__catharsisDeclencheur = b;
    }, true);

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action$="/cart/add"]');
      if (!form || form.hasAttribute('data-no-ajax')) return;
      e.preventDefault();

      var declencheur = e.submitter || form.__catharsisDeclencheur || null;
      form.__catharsisDeclencheur = null;
      // Le bouton de paiement direct porte `name="return_to" value="/checkout"` : sans
      // JavaScript, c'est le champ cache que Shopify attend et le navigateur arrive au paiement
      // tout seul. Avec JavaScript, `new FormData(form)` n'emporte PAS le couple nom/valeur du
      // bouton soumetteur — la redirection est donc a notre charge, ici.
      var achatDirect = !!(declencheur && declencheur.hasAttribute('data-buy-now'));
      var destination = achatDirect ? (declencheur.value || '/checkout') : null;
      if (form.catharsisBusy) return;
      reportCartError(form, '');
      setPurchaseBusy(form, true);
      var productSection = form.closest('[data-product-section]');
      var offer = productSection && productSection.catharsisOffer;
      var transactions = window.CatharsisCartTransactions;
      var hasOfferPicker = productSection && productSection.querySelector('catharsis-packs');
      if (hasOfferPicker && (!offer || !transactions)) {
        // A Pack may never fall through to the generic one-line add while its guarded
        // transaction module is still loading; that would create a paid/missing gift pair.
        reportCartError(form, 'La préparation de l’offre n’est pas terminée. Réessayez dans un instant.');
        setPurchaseBusy(form, false);
        return;
      }
      if (achatDirect && !transactions) {
        reportCartError(form, 'La préparation du panier n’est pas terminée. Réessayez dans un instant.');
        setPurchaseBusy(form, false);
        return;
      }
      enqueueCart(function () {
        var preflight = achatDirect
          ? transactions.cartApi().read().then(function (cart) { assertFrenchOffersReady(cart, transactions); })
          : Promise.resolve();
        return preflight.then(function () {
          return offer && transactions
            ? transactions.runOfferTransaction({ form: form, offer: offer }).then(function () { return refreshCartDrawer(); })
            : addGenericItem(form);
        });
      })
        .then(function (cart) {
          if (achatDirect) {
            assertFrenchOffersReady(cart, transactions);
            window.location.assign(destination);
            return;
          }
          var drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.open();
          setPurchaseBusy(form, false);
        })
        .catch(function (error) {
          reportCartError(form, error.message || 'Le panier n’a pas pu être confirmé. Vérifiez votre panier puis réessayez.');
          setPurchaseBusy(form, false);
        });
    });
  }

  // One-tap add from the cart drawer's cross-sell rows.
  //
  // Same endpoint and same refresh as everything else that touches the cart, so the drawer can
  // never end up showing a total that disagrees with what is in it.
  function initCrossSell() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-xsell-add]');
      if (!btn) return;
      e.preventDefault();
      btn.setAttribute('aria-busy', 'true');
      // data-xsell-add porte un id de variante, ou plusieurs séparés par des virgules
      // (offre groupée : un geste, une seule requête, un seul rafraîchissement du panier).
      var items = btn.getAttribute('data-xsell-add').split(',').map(function (id) {
        return { id: id.trim(), quantity: 1 };
      });
      enqueueCart(function () { return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () { return refreshCartDrawer(); })
        .then(function () {
          // Hors du panneau (fiche produit), l'ouvrir montre le résultat du geste ;
          // dans le panneau, il est déjà ouvert et la rangée vient de disparaître.
          if (btn.hasAttribute('data-xsell-open')) {
            var drawer = document.querySelector('cart-drawer');
            if (drawer) drawer.open();
          }
          btn.removeAttribute('aria-busy');
        })
        .catch(function () { btn.removeAttribute('aria-busy'); }); });
    });
  }

  function initCartLines() {
    try {
      var pendingMessage = sessionStorage.getItem('catharsis:cart-error');
      if (pendingMessage) {
        sessionStorage.removeItem('catharsis:cart-error');
        showCartMutationError(pendingMessage);
      }
    } catch (_) { /* browser storage is optional */ }
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty]');
      if (!input || cartMutationBusy) return;
      var key = input.getAttribute('data-cart-qty');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeLine(key, qty, input.getAttribute('data-cart-attempt-id'), input.getAttribute('data-cart-offer-id'));
    });
    document.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-cart-remove]');
      if (!rm) return;
      e.preventDefault();
      if (cartMutationBusy) return;
      changeLine(rm.getAttribute('data-cart-remove'), 0, rm.getAttribute('data-cart-attempt-id'), rm.getAttribute('data-cart-offer-id'));
    });
  }

  function initCheckoutGuard() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('form[action$="/cart"]');
      var submitter = event.submitter || form?.__catharsisDeclencheur;
      if (form && cartMutationBusy) { event.preventDefault(); return; }
      if (!form || !submitter || submitter.name !== 'checkout' || form.dataset.catharsisCheckoutConfirmed === 'true') return;
      var transactions = window.CatharsisCartTransactions;
      if (!transactions) {
        if (document.querySelector('[data-cart-offer-id="france"]')) {
          event.preventDefault();
          reportCartError(form, cartMutationMessage(new Error('La préparation du panier n’est pas terminée. Réessayez dans un instant.')));
        }
        return;
      }
      event.preventDefault();
      if (form.catharsisBusy) return;
      setPurchaseBusy(form, true);
      enqueueCart(function () { return transactions.cartApi().read(); })
        .then(function (cart) {
          assertFrenchOffersReady(cart, transactions);
          form.dataset.catharsisCheckoutConfirmed = 'true';
          var confirmation = document.createElement('input');
          confirmation.type = 'hidden';
          confirmation.name = 'checkout';
          confirmation.value = '';
          form.appendChild(confirmation);
          HTMLFormElement.prototype.submit.call(form);
        })
        .catch(function (error) {
          reportCartError(form, error.message || 'Le panier n’a pas pu être confirmé.');
          setPurchaseBusy(form, false);
        });
    });
  }

  /* ---------- Cart upsell include toggle (founder-gated, EU-flagged) ---------- */
  // Only relevant when the founder enables the pre-checked include checkbox in the cart
  // drawer (snippets/cart-upsell.liquid). The explicit "Ajouter" button is the SOLE add
  // mechanism; this handler merely enables/disables it from the include checkbox so the
  // shopper can opt out. It NEVER adds to cart on its own — nothing is charged without the
  // explicit button click. Acts on user `change` events only, never on load.
  function initCartUpsell() {
    document.addEventListener('change', function (e) {
      var box = e.target.closest('[data-upsell-include]');
      if (!box) return;
      var card = box.closest('[data-cart-upsell]');
      if (!card) return;
      var addBtn = card.querySelector('[data-upsell-add]');
      if (!addBtn) return;
      addBtn.disabled = !box.checked;
      addBtn.setAttribute('aria-disabled', String(!box.checked));
    });
  }

  /* ---------- Honest countdown island ---------- */
  // Counts down to a FIXED absolute instant read from data-countdown-until (ISO 8601 with a
  // timezone offset, e.g. 2026-07-01T23:59:00+02:00). Honest by construction:
  //   - NO localStorage / cookies, NO per-session or rolling "X hours from first visit" logic.
  //   - It reads ONLY the server-set target, so a reload shows the SAME remaining time minus
  //     the real seconds elapsed — never a fresh full timer.
  //   - On expiry it sets [hidden] and stops. It NEVER resets or loops back to a full count.
  // Missing, unparseable (NaN), or already-past targets are hidden immediately with no ticker.
  // A single shared interval drives every instance and is cleared once none remain.
  function initCountdowns() {
    var nodes = document.querySelectorAll('[data-countdown-until]');
    if (!nodes.length) return;
    var timers = [];
    var ticker = null;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    // Swap the pre-hydration static deadline for the live clock (no-JS keeps the static line).
    function reveal(el) {
      var clock = el.querySelector('[data-countdown-clock]');
      var fallback = el.querySelector('[data-countdown-fallback]');
      if (clock) clock.hidden = false;
      if (fallback) fallback.hidden = true;
    }

    // Returns false once the instant has passed → the element is hidden and dropped from the loop.
    function paint(t) {
      var remain = t.target - Date.now();
      if (remain <= 0) { t.el.setAttribute('hidden', ''); return false; }
      var total = Math.floor(remain / 1000);
      var days = Math.floor(total / 86400);
      var hours = Math.floor((total % 86400) / 3600);
      var minutes = Math.floor((total % 3600) / 60);
      var seconds = total % 60;
      if (t.d) t.d.textContent = pad(days);
      if (t.h) t.h.textContent = pad(hours);
      if (t.m) t.m.textContent = pad(minutes);
      if (t.s) t.s.textContent = pad(seconds);
      return true;
    }

    function onTick() {
      for (var i = timers.length - 1; i >= 0; i--) {
        if (!paint(timers[i])) timers.splice(i, 1);
      }
      if (!timers.length && ticker) { clearInterval(ticker); ticker = null; }
    }

    nodes.forEach(function (el) {
      var raw = el.getAttribute('data-countdown-until');
      var target = raw ? new Date(raw).getTime() : NaN;
      // Honesty + robustness: only a valid future instant starts a ticker.
      if (!raw || isNaN(target) || target <= Date.now()) {
        el.setAttribute('hidden', '');
        return;
      }
      var t = {
        el: el,
        target: target,
        d: el.querySelector('[data-countdown-days]'),
        h: el.querySelector('[data-countdown-hours]'),
        m: el.querySelector('[data-countdown-minutes]'),
        s: el.querySelector('[data-countdown-seconds]')
      };
      reveal(el);
      paint(t);
      timers.push(t);
    });

    if (timers.length) ticker = setInterval(onTick, 1000);
  }

  /* ---------- Ticker / marquee (premium, honest) ----------
     Progressive enhancement for sections/ticker.liquid. The section ships a STATIC, centred,
     wrapping list (every founder-entered statement visible) — that is the no-JS / reduced-motion
     fallback and the crawler/AT source of truth. This island only upgrades it to a slow looping
     marquee, and only when it earns it:
       - bails entirely under prefers-reduced-motion (poster-equivalent: the static list stays),
       - measures the set; animates ONLY when the set overflows its viewport (so a few short USPs
         on desktop stay calmly centred, never scroll),
       - clones the set once (aria-hidden, removed from the a11y tree and read once by crawlers)
         so translateX(-50%) loops with no seam, then adds .ticker--animated.
     Re-measures on resize (debounced) and reverses the upgrade if content later fits. */
  var tickerInstances = new Map();
  function initTicker(root) {
    if (reduceMotion) return;
    var tickers = nodesWithin(root, '[data-ticker]');
    if (!tickers.length) return;
    tickers.forEach(function (ticker) {
      if (tickerInstances.has(ticker)) return;
      var viewport = ticker.querySelector('[data-ticker-viewport]');
      var track = ticker.querySelector('[data-ticker-track]');
      var set = ticker.querySelector('[data-ticker-set]');
      if (!viewport || !track || !set) return;
      var clone = null;

      function update() {
        var overflows = set.scrollWidth > viewport.clientWidth + 1;
        if (overflows && !clone) {
          clone = set.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-ticker-set');
          track.appendChild(clone);
          ticker.classList.add('ticker--animated');
        } else if (!overflows && clone) {
          clone.remove();
          clone = null;
          ticker.classList.remove('ticker--animated');
        }
      }

      update();
      var rt = null;
      var onResize = function () {
        clearTimeout(rt);
        rt = setTimeout(update, 200);
      };
      window.addEventListener('resize', onResize, { passive: true });
      tickerInstances.set(ticker, function () {
        clearTimeout(rt);
        window.removeEventListener('resize', onResize);
        if (clone) clone.remove();
        ticker.classList.remove('ticker--animated');
      });
    });
  }

  function destroyTicker(root) {
    nodesWithin(root, '[data-ticker]').forEach(function (ticker) {
      var destroy = tickerInstances.get(ticker);
      if (!destroy) return;
      destroy();
      tickerInstances.delete(ticker);
    });
  }

  /* ---------- Before / after comparison slider ----------
     Progressive enhancement for sections/before-after.liquid. No-JS = a static, informative 50/50
     split. This island reveals the native range control (.before-after--ready), then mirrors the
     range value onto the --reveal custom property (which clips the "before" layer + positions the
     divider) and keeps aria-valuetext localized. The full-area range gives click/drag-anywhere for
     free (mouse + touch); touch-action:pan-y leaves vertical page scroll intact. Nothing animates on
     its own, so reduced-motion needs no special handling. */
  function initBeforeAfter() {
    var nodes = document.querySelectorAll('[data-before-after]');
    if (!nodes.length) return;
    nodes.forEach(function (ba) {
      var range = ba.querySelector('[data-before-after-range]');
      if (!range) return;
      var template = range.getAttribute('data-valuetext-template') || '__PERCENT__%';
      ba.classList.add('before-after--ready');
      function apply() {
        var v = range.value;
        ba.style.setProperty('--reveal', v + '%');
        range.setAttribute('aria-valuetext', template.replace('__PERCENT__', v));
      }
      range.addEventListener('input', apply);
      apply();
    });
  }

  /* ---------- Shoppable image hotspots (popover) ----------
     Progressive enhancement for sections/shoppable-image.liquid. No-JS = each hotspot is a plain
     link to its product. This island intercepts the link to toggle an accessible product popover
     (role="dialog") instead: aria-expanded on the trigger, focus moved into the panel, ESC /
     outside-click close + focus return. The panel is clamped inside the frame (flips above/below,
     shifts horizontally) and re-clamped on resize. Nothing animates on its own beyond the calm CSS
     pulse, which is itself reduced-motion-gated. */
  function initShoppable() {
    var triggers = document.querySelectorAll('[data-hotspot-trigger]');
    if (!triggers.length) return;
    var current = null;

    function close() {
      if (!current) return;
      current.panel.hidden = true;
      current.trigger.setAttribute('aria-expanded', 'false');
      current = null;
    }

    function position(trigger, panel) {
      var frame = trigger.closest('[data-shoppable-frame]');
      if (!frame) return;
      var fr = frame.getBoundingClientRect();
      var tr = trigger.getBoundingClientRect();
      var cx = tr.left - fr.left + tr.width / 2;
      var cy = tr.top - fr.top + tr.height / 2;
      var pw = panel.offsetWidth;
      var ph = panel.offsetHeight;
      var fh = frame.clientHeight;
      var left = Math.max(8, Math.min(cx - pw / 2, frame.clientWidth - pw - 8));
      // Au-dessus du point, sinon en dessous, puis BORNE au cadre dans les deux cas. La version
      // precedente ne bornait jamais la hauteur : sous le point, la carte sortait du cadre et
      // `overflow: hidden` la coupait (mesure du 13 septembre : 304 px rognes a 425 px de large).
      var top = cy - tr.height / 2 - ph - 14;
      if (top < 8) top = cy + tr.height / 2 + 14;
      if (top + ph > fh - 8) top = Math.max(8, fh - ph - 8);
      panel.style.left = left + 'px';
      panel.style.top = top + 'px';
    }

    triggers.forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var willOpen = panel.hidden;
        close();
        if (willOpen) {
          panel.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          current = { panel: panel, trigger: trigger };
          position(trigger, panel);
          var focusable = panel.querySelector('a, button');
          // `preventScroll` : sans lui, donner le focus a la carte faisait sauter la page.
          if (focusable) focusable.focus({ preventScroll: true });
        }
      });
      var closeBtn = panel.querySelector('[data-hotspot-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { var t = trigger; close(); t.focus(); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && current) { var t = current.trigger; close(); t.focus(); }
    });
    document.addEventListener('click', function (e) {
      if (current && !e.target.closest('[data-hotspot-trigger]') && !e.target.closest('[data-hotspot-popover]')) close();
    });
    window.addEventListener('resize', function () {
      if (current) position(current.trigger, current.panel);
    }, { passive: true });
  }

  /* ---------- Localization selector (country/currency + language) ----------
     Progressive enhancement for snippets/localization-selector.liquid. The form
     submits natively; a no-JS shopper uses the <noscript> submit button. With JS,
     changing any [data-localization-form] <select> auto-submits its own form, so
     the button stays in <noscript> only (no duplicate affordance, no inline
     onchange). Delegated once on the document — works for the header and footer
     instances and survives section re-renders. */
  function initLocalizationForm() {
    document.addEventListener('change', function (e) {
      var select = e.target.closest('select[data-localization-form]');
      if (!select || !select.form) return;
      select.form.submit();
    });
  }

  /* ---------- Cart reservation countdown (anchored, never resets) ----------
     Drives the `declared_hold` mode of sections/cart-drawer.liquid. What the platform actually
     enforces is recorded in that section's doc header: adding to cart reserves nothing — Shopify
     holds inventory only when the customer submits payment information. The countdown is therefore
     a MERCHANT promise, and the one thing this code can guarantee is that it never manufactures
     urgency:
       - The start instant is anchored to the first time THIS cart (its cart token) was seen with
         items in it, persisted in localStorage. Navigating, reloading, reopening the drawer or
         re-rendering it through the Section Rendering API continues the SAME countdown; only a new
         cart token opens a new window. No code path restarts a running clock.
       - Liquid already rendered the full duration, zero-padded, so the first paint is correct with
         JS disabled and this island only rewrites the digits — no empty placeholder, no reflow.
       - On expiry the cart is untouched: nothing emptied, no line removed. The founder's setting
         only chooses between a neutral sentence and hiding the line.
       - Plain text writes, no animation — identical under prefers-reduced-motion.
       - Shopify's own cookies are never read or written; the anchor lives in our own key.
     Storage unavailable (private mode) degrades to anchoring at this pageview, the only honest
     fallback when nothing can be remembered. */
  function initCartReservation() {
    var KEY = 'catharsis:reservation';
    // Session fallback for blocked storage (private mode, zero quota). anchor() is called from
    // start(), and start() re-runs on every cart mutation via the drawer observer — so without a
    // module-scoped memory the clock jumped back to full duration each time an item was added.
    var memAnchor = null;
    var memToken = null;
    var ticker = null;
    var observer = null;
    var observed = null;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    // First sighting of this cart token stamps the anchor; every later call reads it back.
    function anchor(token) {
      var now = Date.now();
      try {
        var raw = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (raw && raw.token === token && typeof raw.at === 'number') return raw.at;
        localStorage.setItem(KEY, JSON.stringify({ token: token, at: now }));
        return now;
      } catch (e) { /* storage blocked → fall through to the in-memory anchor */ }
      if (memToken === token && typeof memAnchor === 'number') return memAnchor;
      memToken = token; memAnchor = now;
      return now;
    }

    function stop() { if (ticker) { clearInterval(ticker); ticker = null; } }

    function start() {
      stop();
      var el = document.querySelector('[data-cart-reservation]');
      if (!el) return;
      var out = el.querySelector('[data-reservation-text]');
      var minutes = parseInt(el.getAttribute('data-reservation-minutes'), 10);
      var token = el.getAttribute('data-cart-token') || 'empty';
      if (!out || !minutes || token === 'empty') return;

      var onExpiry = el.getAttribute('data-reservation-expiry') || 'neutral';
      var expiredText = el.getAttribute('data-expired-text') || '';
      var template = out.textContent; // server-rendered sentence carrying the full duration
      var endsAt = anchor(token) + minutes * 60000;
      var done = false;

      function paint() {
        var left = endsAt - Date.now();
        if (left <= 0) {
          done = true;
          stop();
          if (onExpiry === 'hide') el.hidden = true;
          else if (expiredText) out.textContent = expiredText;
          return;
        }
        var total = Math.floor(left / 1000);
        out.textContent = template.replace(/\d{1,2}:\d{2}/, pad(Math.floor(total / 60)) + ':' + pad(total % 60));
      }

      paint();
      if (done) return;
      ticker = setInterval(paint, 1000);
      // A backgrounded tab does not need a per-second repaint; resume with one immediate paint so
      // the displayed time is never stale.
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') { stop(); }
        else if (!ticker && !done) { paint(); if (!done) ticker = setInterval(paint, 1000); }
      });
    }

    // The drawer panel's innerHTML is replaced wholesale by the Section Rendering API after every
    // cart mutation, which destroys the reservation node. Observe ONLY the panel's direct children
    // (no subtree) so that swap re-arms the island, while the per-second text write — a deep
    // mutation — can never feed back into the observer.
    function watch() {
      if (!('MutationObserver' in window)) return;
      var panel = document.querySelector('cart-drawer .cart-drawer__panel');
      if (!panel || panel === observed) return;
      if (observer) observer.disconnect();
      observer = new MutationObserver(function () { start(); });
      observer.observe(panel, { childList: true });
      observed = panel;
    }

    watch();
    start();
    // Theme Editor: re-rendering the section replaces the panel node itself.
    document.addEventListener('shopify:section:load', function () { observed = null; watch(); start(); });
  }

  /* ---------- Theme Editor section lifecycle ----------
     The editor replaces a section's subtree instead of reloading global.js. Keep the scan-based
     islands below scoped and idempotent: replacement gets fresh observers/enhancement; unload drops
     references and resize listeners from the detached subtree. Reveal is immediate in the editor so
     a newly inserted section can never remain opacity:0 while the observer waits for a paint. */
  function initEditorSectionLifecycle() {
    function hydrate(root, revealImmediately) {
      initReveal(root, revealImmediately);
      initHeroVideo(root);
      initTicker(root);
    }
    document.addEventListener('shopify:section:load', function (e) { hydrate(e.target, true); });
    document.addEventListener('shopify:section:select', function (e) { hydrate(e.target, true); });
    document.addEventListener('shopify:section:unload', function (e) {
      destroyReveal(e.target);
      destroyHeroVideo(e.target);
      destroyTicker(e.target);
    });
  }

  function init() {
    document.addEventListener('cart:updated', function (event) {
      if (!cartHasFranceOffer(event.detail || {})) return;
      document.querySelectorAll('[data-dynamic-checkout-slot], [data-dynamic-checkout-source]')
        .forEach(function (node) { node.replaceChildren(); });
    });
    initReveal();
    initHeroVideo();
    initHeader();
    initTicker();
    initBeforeAfter();
    initShoppable();
    initLocalizationForm();
    initQtySteppers();
    initAccordions();
    initTabs();
    initCountdowns();
    initPredictiveSearch();
    initAjaxCart();
    initCartLines();
    initCheckoutGuard();
    initCrossSell();
    initCartUpsell();
    initCartReservation();
    initEditorSectionLifecycle();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
